import type { UniclientAction } from '../action';
import type { FormattedText } from '../utils';
import type { TagComposerProps } from './tag';
import type { UniclientBaseComponentProps, UniclientWizardComponentBase } from './types';

export type SelectableLayout = 'column' | 'row';
export type SelectableMode = 'multiple' | 'single';
export type SelectablePresentation = 'buttons' | 'cards' | 'labels';
export type SelectableIllustration = Record<SelectableLayout, string> | string;

export type SelectableValueType = boolean | number | string;

export interface BaseSelectableOptionProps {
  readonly value: SelectableValueType;
  readonly title: FormattedText;
  readonly disabled?: boolean;
  readonly loading?: boolean;
}

export interface ButtonSelectableOptionProps extends BaseSelectableOptionProps {
  readonly presentation: 'buttons';
  readonly subtitle?: never;
  readonly thumbnail?: never;
  readonly selectedThumbnail?: never;
  /**
   * Single mode: Action triggers on card click, replacing selection behavior
   * Multiple mode: Action shows as separate button (when no subtitle), allowing triggering a separate action for the card like a opening a dialog.
   */
  readonly action?: UniclientAction;
  readonly tag?: never;
}

interface ButtonsSelectableProps<TAttribute extends string = string> {
  readonly attribute: TAttribute;
  readonly presentation: 'buttons';
  readonly mode?: 'single';
  readonly layout?: 'column';
  readonly mobileRowLayout?: never;
  readonly triggerAction?: never;
  readonly bordered?: never;
  readonly showThumbnailBackground?: never;
  readonly thumbnailSize?: never;
  readonly options: ButtonSelectableOptionProps[];
}

export interface CardSelectableOptionProps extends BaseSelectableOptionProps {
  readonly presentation: 'cards';
  readonly subtitle?: FormattedText;
  readonly action?: UniclientAction;
  readonly thumbnail?: SelectableIllustration;
  readonly selectedThumbnail?: SelectableIllustration;
  readonly tag?: TagComposerProps;
}

interface CardsSelectableProps<
  TAttribute extends string = string,
  TLayout extends SelectableLayout = SelectableLayout,
  TMode extends SelectableMode = SelectableMode,
> {
  readonly attribute: TAttribute;
  readonly presentation: 'cards';
  readonly mode?: TMode;
  readonly layout?: TLayout;
  readonly mobileRowLayout?: NoInfer<TLayout> extends 'column' ? never : boolean;
  readonly triggerAction?: NoInfer<TMode> extends 'multiple' ? never : boolean;
  readonly showThumbnailBackground?: boolean;
  readonly thumbnailSize?: 'lg' | 'sm';
  readonly bordered?: never;
  readonly options: CardSelectableOptionProps[];
}

export interface LabelsSelectableOptionProps<
  TThumbnail extends SelectableIllustration | undefined = SelectableIllustration | undefined,
> extends BaseSelectableOptionProps {
  readonly presentation: 'labels';
  readonly subtitle?: FormattedText;
  readonly thumbnail?: TThumbnail;
  readonly selectedThumbnail?: TThumbnail;
  readonly action?: never;
  readonly tag?: never;
}

interface LabelsSelectableProps<TAttribute extends string = string, TBordered extends boolean = boolean> {
  readonly attribute: TAttribute;
  readonly presentation: 'labels';
  readonly mode?: SelectableMode;
  readonly layout?: 'column';
  readonly mobileRowLayout?: never;
  readonly triggerAction?: never;
  readonly showThumbnailBackground?: never;
  readonly thumbnailSize?: never;
  readonly bordered?: TBordered;
  readonly options: LabelsSelectableOptionProps<
    NoInfer<TBordered> extends false | undefined ? undefined : SelectableIllustration | undefined
  >[];
}

export type SelectableOptionProps = Omit<
  ButtonSelectableOptionProps | CardSelectableOptionProps | LabelsSelectableOptionProps,
  'presentation'
>;

export type SelectableProps<
  TAttribute extends string = string,
  TLayout extends SelectableLayout = SelectableLayout,
  TMode extends SelectableMode = SelectableMode,
  TBordered extends boolean = boolean,
> = UniclientBaseComponentProps &
  (
    | ButtonsSelectableProps<TAttribute>
    | CardsSelectableProps<TAttribute, TLayout, TMode>
    | LabelsSelectableProps<TAttribute, TBordered>
  );

export type WizardSelectableProps<
  TAttribute extends string = string,
  TLayout extends SelectableLayout = SelectableLayout,
  TMode extends SelectableMode = SelectableMode,
  TBordered extends boolean = boolean,
> = Omit<SelectableProps<TAttribute, TLayout, TMode, TBordered>, 'options'> &
  UniclientWizardComponentBase<'selectable'> & {
    readonly options: SelectableOptionProps[];
  };

export function selectable<
  TAttribute extends string = string,
  TLayout extends SelectableLayout = 'column',
  TMode extends SelectableMode = 'single',
  TBordered extends boolean = false,
>(
  props: SelectableProps<TAttribute, TLayout, TMode, TBordered>,
): WizardSelectableProps<TAttribute, TLayout, TMode, TBordered> {
  return {
    ...props,
    type: 'selectable',
  };
}

export function selectableButton(
  props: Omit<ButtonSelectableOptionProps, 'presentation' | 'selectedThumbnail' | 'subtitle' | 'thumbnail'>,
): ButtonSelectableOptionProps {
  return {
    ...props,
    presentation: 'buttons',
  };
}

export function selectableCard(props: Omit<CardSelectableOptionProps, 'presentation'>): CardSelectableOptionProps {
  return {
    ...props,
    presentation: 'cards',
  };
}

export function selectableLabel<TThumbnail extends SelectableIllustration | undefined = undefined>(
  props: Omit<LabelsSelectableOptionProps<TThumbnail>, 'action' | 'presentation'>,
): LabelsSelectableOptionProps<NoInfer<TThumbnail>> {
  return { ...props, presentation: 'labels' };
}

selectable.button = selectableButton;
selectable.card = selectableCard;
selectable.label = selectableLabel;
